<template>
  <div class="input-signature">
    <span
      v-if="label"
      class="input-signature__label"
    >
      {{ formattedLabel }}
    </span>

    <template v-if="isSignatureInSignMode">
      <Vue3Signature
        ref="currentSignature"
        class="input-signature__canvas"
        :w="width"
        :h="height"
      />

      <f7-row>
        <f7-col>
          <f7-button
            :round="areButtonsRounded"
            small
            outline
            color="gray"
            @click="clearSignature"
          >
            {{ $t('common.clear') }}
          </f7-button>
        </f7-col>

        <f7-col>
          <f7-button
            class="input-signature__save"
            :round="areButtonsRounded"
            color="cyan"
            fill
            small
            @click="saveSignature"
          >
            {{ $t('common.sign') }}
          </f7-button>
        </f7-col>
      </f7-row>
    </template>

    <template v-else>
      <img
        class="input-signature__signature"
        :src="signatureImg"
        alt="signature"
        :width="width"
        :height="height"
      >

      <f7-row>
        <f7-col>
          <f7-button
            class="input-signature__edit"
            :round="areButtonsRounded"
            color="cyan"
            fill
            small
            @click="editSignature"
          >
            {{ $t('common.edit') }}
          </f7-button>
        </f7-col>
      </f7-row>
    </template>
  </div>
</template>

<script setup>
// @ts-check
import {
  computed,
  ref,
  shallowRef,
  toRefs,
  watch,
} from 'vue';

import Vue3Signature from 'vue3-signature';

/** @import { ShallowRef } from 'vue' */

const props = defineProps({
  label: { type: String, default: null },
  areButtonsRounded: { type: Boolean, required: true },
  required: { type: Boolean, default: false },
  signature: { type: String, default: '' },
  width: { type: String, default: '354px' },
  height: { type: String, default: '202px' },
});

const emit = defineEmits([
  'newSignature',
  // TODO: Emit this event when the signature is cleared
  'signatureCleared',
]);

const {
  label,
  areButtonsRounded,
  required,
  signature,
} = toRefs(props);

/** @type {ShallowRef<InstanceType<typeof Vue3Signature> | null>} */
const currentSignature = shallowRef(null);
const signatureImg = ref('');
const isSignatureInSignMode = ref(true);

const formattedLabel = computed(() => (required.value ? `${label.value}*` : label.value));

watch(signature, (newVal) => {
  if (newVal) {
    signatureImg.value = newVal;
    isSignatureInSignMode.value = false;
  }
});

function saveSignature() {
  if (currentSignature.value) {
    signatureImg.value = currentSignature.value.save('image/jpeg');
  }
  emit('newSignature', signatureImg.value);

  isSignatureInSignMode.value = false;
}

function clearSignature() {
  if (currentSignature.value) {
    currentSignature.value.clear();
    emit('signatureCleared');
  }

  signatureImg.value = '';
}

function editSignature() {
  isSignatureInSignMode.value = true;
  clearSignature();
}
</script>

<style scoped lang="scss">
.input-signature {
  display: flex;
  flex-direction: column;

  &__label {
    font-size: 14px;
    font-weight: bold;
    color: #253858;
  }

  &__canvas {
    border: 1px solid var(--his-silver);
    border-radius: 8px;
    margin-bottom: 15px;
  }

  &__signature {
    border: 1px solid var(--his-silver);
    border-radius: 8px;
    margin-bottom: 15px;
  }

  &__edit,
  &__save {
    color: #fff;
  }
}
</style>
